import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { ExerciseMedia } from '@/components/ExerciseMedia';
import { Check, Pause, Play, Repeat, SkipForward, Square, Plus, TrendingUp, Volume2 } from 'lucide-react';
import { VOICE_OPTIONS } from '@/lib/voiceCoach';
import { workDisplay } from './useGuidedEngine';

const fmt = (s) => { const t = Math.max(0, Math.ceil(s)); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`; };
const BTN = 'h-12 rounded-xl font-semibold';

const INTRO = {
  strength: ['Voice-guided session', 'Your coach sets the pace of every rep, counts each one exactly and times your rest. Keep your phone where you can hear it.'],
  circuit: ['Voice-guided circuit', 'Start once. Your coach announces every exercise, counts your work, runs the programmed recovery and moves you through each round.'],
  race: ['Voice-guided race', 'Start once. Your coach calls each station and runs a short transition so you can get set. Skip it when you are ready, or pause for more time.'],
};

export function IntroView({ loaded, total, ready, voiceOn, onBegin, kind = 'strength', modeSlot = null, locked = false }) {
  const [title, text] = INTRO[kind] || INTRO.strength;
  return (
    <div className="text-center py-6" data-testid="guided-intro">
      <div className="w-16 h-16 rounded-full bg-[#00C8FF]/15 flex items-center justify-center mx-auto"><Volume2 className="w-8 h-8 text-[#00C8FF]" /></div>
      <div className="font-display font-black text-2xl mt-4">{title}</div>
      <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">{text}</p>
      {modeSlot}
      {voiceOn && (
        <div className="mt-5 text-xs text-muted-foreground font-mono" data-testid="guided-prefetch">
          {ready ? 'Coach ready' : `Preparing your coach… ${loaded}/${total}`}
          <div className="h-1.5 rounded-full bg-[var(--c-film)] mt-2 max-w-xs mx-auto overflow-hidden"><div className="h-full bg-[#00C8FF] transition-[width] duration-300" style={{ width: `${total ? (loaded / total) * 100 : 0}%` }} /></div>
        </div>
      )}
      <Button onClick={onBegin} disabled={!ready || locked} data-testid="guided-begin" className={`${BTN} mt-6 w-full bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726]`}>
        <Play className="w-5 h-5 mr-2" /> {ready ? 'Begin workout' : 'Getting ready…'}
      </Button>
    </div>
  );
}

function WeightBlock({ item, weight, setWeight }) {
  return (
    <div className="mt-3 space-y-2">
      {item.load_increase && item.suggested_weight && weight !== item.suggested_weight && (
        <div className="p-3 rounded-xl bg-[#00C8FF]/8 border border-[#00C8FF]/25" data-testid="guided-weight-suggestion">
          <div className="flex items-center gap-1.5 text-[#00C8FF] label-mini"><TrendingUp className="w-3.5 h-3.5" /> Heavier week</div>
          <p className="text-xs text-muted-foreground mt-1">Your plan steps up the load this week. Last time you lifted {item.previous_weight}. Try {item.suggested_weight}?</p>
          <Button size="sm" onClick={() => setWeight(item.suggested_weight)} data-testid="guided-accept-weight" className="mt-2 bg-[#00C8FF] text-[#081726] h-8">Use {item.suggested_weight}</Button>
        </div>
      )}
      {item.load_increase && !item.suggested_weight && <p className="text-xs text-[#00C8FF]" data-testid="guided-heavier-note">Plan: go slightly heavier than last week, then keep the same control.</p>}
      <label className="flex items-center gap-2"><span className="label-mini shrink-0">Weight</span>
        <Input value={weight} onChange={e => setWeight(e.target.value)} placeholder="e.g. 20 kg" data-testid="guided-weight-input" className="h-9 bg-[var(--c-film)] border-[var(--c-hair)] font-mono" /></label>
    </div>
  );
}

export function SetupView({ cur, weight, setWeight, onReady, onRepeat, onSkip, auto = false, eng }) {
  return (
    <div data-testid="guided-setup">
      {cur.ex?.gif_url ? <ExerciseMedia src={cur.ex.gif_url} alt={cur.name} className="w-full aspect-video rounded-xl" fit="contain" /> : <div className="w-full aspect-video rounded-xl bg-[var(--c-film)]" />}
      <div className="label-mini text-[#00C8FF] mt-3">Get into position</div>
      <div className="font-display font-black text-2xl mt-1" data-testid="guided-ex-name">{cur.name}</div>
      <div className="font-mono text-[#00C8FF] mt-1" data-testid="guided-prescription">
        {cur.round ? `Round ${cur.round} of ${cur.rounds} · ${cur.item.reps}${cur.rest ? ` · ${cur.rest}s ${cur.lastInRound ? 'round rest' : 'after'}` : ''}` : `${cur.sets} sets × ${cur.item.reps} · ${cur.rest}s rest`}
      </div>
      <div className="text-xs text-muted-foreground mt-1" data-testid="guided-tempo">Tempo: {cur.tempo.label}</div>
      <p className="text-sm text-muted-foreground mt-3 leading-relaxed" data-testid="guided-setup-text">{cur.setup}</p>
      {!cur.round && <WeightBlock item={cur.item} weight={weight} setWeight={setWeight} />}
      {auto ? <AutoStart eng={eng} /> : (
        <Button onClick={onReady} data-testid="guided-ready" className={`${BTN} mt-4 w-full bg-[var(--c-green)] hover:bg-[var(--c-green-soft)] text-[#081726]`}><Check className="w-5 h-5 mr-2" /> I'm ready</Button>
      )}
      <div className="grid grid-cols-2 gap-2 mt-2">
        <Button variant="outline" onClick={onRepeat} data-testid="guided-repeat" className="border-[var(--c-hair)]"><Repeat className="w-4 h-4 mr-1.5" /> Repeat instructions</Button>
        <Button variant="outline" onClick={onSkip} data-testid="guided-skip-setup" className="border-[var(--c-hair)]"><SkipForward className="w-4 h-4 mr-1.5" /> Skip exercise</Button>
      </div>
    </div>
  );
}

// Continuous mode: setup auto-advances after the instructions; pause holds it, "Start now" begins early.
function AutoStart({ eng }) {
  const left = Math.max(0, Math.ceil(eng.spec.duration - eng.el));
  return (
    <div className="mt-4" data-testid="guided-autostart">
      <div className="text-xs text-muted-foreground text-center font-mono" data-testid="guided-autostart-left">{eng.paused ? 'Paused — resume when you are set' : `Starting in ${left}s`}</div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <Button variant="outline" onClick={eng.togglePause} data-testid="guided-setup-pause" className="border-[var(--c-hair)] h-11">{eng.paused ? <><Play className="w-4 h-4 mr-1" /> Resume</> : <><Pause className="w-4 h-4 mr-1" /> Pause</>}</Button>
        <Button onClick={eng.ready} data-testid="guided-start-now" className="h-11 bg-[var(--c-green)] hover:bg-[var(--c-green-soft)] text-[#081726] font-semibold"><Check className="w-4 h-4 mr-1" /> Start now</Button>
      </div>
    </div>
  );
}

function Controls({ paused, onPause, onSkip, onStop }) {
  return (
    <div className="grid grid-cols-3 gap-2 mt-5">
      <Button variant="outline" onClick={onPause} data-testid="guided-pause" className="border-[var(--c-hair)] h-11">{paused ? <><Play className="w-4 h-4 mr-1" /> Resume</> : <><Pause className="w-4 h-4 mr-1" /> Pause</>}</Button>
      <Button variant="outline" onClick={onSkip} data-testid="guided-skip" className="border-[var(--c-hair)] h-11"><SkipForward className="w-4 h-4 mr-1" /> Skip</Button>
      <Button variant="outline" onClick={onStop} data-testid="guided-stop" className="border-[var(--c-hair)] h-11 text-[#FF5C5C]"><Square className="w-4 h-4 mr-1" /> Stop</Button>
    </div>
  );
}

function CountdownRing({ value, progress, testid }) {
  const r = 26, c = 2 * Math.PI * r;
  return (
    <div className="absolute top-2 right-2 w-[68px] h-[68px] rounded-full bg-[#05101C]/75 backdrop-blur-sm flex items-center justify-center" data-testid="guided-countdown-ring">
      <svg className="absolute inset-0 -rotate-90" viewBox="0 0 68 68" aria-hidden="true">
        <circle cx="34" cy="34" r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="5" />
        <circle cx="34" cy="34" r={r} fill="none" stroke="#00C8FF" strokeWidth="5" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(1, progress || 0))} />
      </svg>
      <span className={`relative font-display font-black text-white tabular-nums ${String(value).includes(':') ? 'text-sm' : 'text-2xl'}`} data-testid={testid}>{value}</span>
    </div>
  );
}

export function WorkView({ eng }) {
  const { cur, el, st, spec, paused } = eng;
  const d = workDisplay(cur, el, spec.duration);
  const media = cur.ex?.gif_url;
  // Ring = the live countdown: lead-in seconds, seconds left in the movement phase, or seconds left in timed work.
  const ringVal = d.lead ? d.count : d.timed ? d.remaining : d.open ? fmt(d.elapsed) : d.secs ? Math.max(1, d.secs - d.second + 1) : d.rep;
  const ringProg = d.lead ? 1 - (d.count - 1) / 3 : d.progress;
  const setLine = cur.round ? `Round ${cur.round} of ${cur.rounds}` : `Set ${st.set + 1} of ${cur.sets}`;
  const repLine = d.lead ? 'Get ready' : d.timed ? `${d.remaining}s left of ${cur.target}s` : d.open ? `${cur.item.reps} · your pace` : `Rep ${d.rep} of ${cur.target}`;
  return (
    <div data-testid="guided-work">
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-display font-black text-xl leading-tight truncate" data-testid="guided-work-name">{cur.name}</div>
        <div className="font-mono text-xs text-[#00C8FF] shrink-0" data-testid="guided-work-where">{setLine}</div>
      </div>
      <div className="relative -mx-5 sm:mx-0 mt-2 bg-[#05101C] sm:rounded-xl overflow-hidden" data-testid="guided-video">
        {media
          ? <ExerciseMedia key={media} src={media} alt={cur.name} className="w-full aspect-square max-h-[58vh]" fit="contain" />
          : <div className="w-full aspect-[4/3] flex items-center justify-center text-muted-foreground text-xs">No demo video for this exercise</div>}
        <CountdownRing value={ringVal} progress={ringProg} testid="guided-rep-count" />
        {paused && <div className="absolute top-2 left-2 pill bg-[#FFB800] text-[#081726]" data-testid="guided-paused">Paused</div>}
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-black text-2xl tabular-nums leading-none" data-testid="guided-rep-label">{repLine}</div>
          {cur.tempo.kind === 'tempo' && <div className="text-[11px] text-muted-foreground mt-1">Tempo {cur.tempo.label}</div>}
        </div>
        {!d.lead && d.cue && (
          <div className="font-display font-bold text-2xl text-[#00C8FF] shrink-0" data-testid="guided-phase-cue">{d.cue}{d.secs > 1 ? ` · ${Math.max(1, d.secs - d.second + 1)}` : ''}</div>
        )}
      </div>
      {!d.lead && (d.cue || d.timed) && <div className="h-2 rounded-full bg-[var(--c-film)] mt-2 overflow-hidden"><div className="h-full bg-[#00C8FF]" style={{ width: `${(d.progress || 0) * 100}%` }} /></div>}
      {d.open && <Button onClick={eng.stationDone} data-testid="guided-station-done" className={`${BTN} mt-4 w-full bg-[var(--c-green)] hover:bg-[var(--c-green-soft)] text-[#081726]`}><Check className="w-5 h-5 mr-2" /> Station done</Button>}
      <Controls paused={paused} onPause={eng.togglePause} onSkip={eng.skipExercise} onStop={eng.stop} />
    </div>
  );
}

export function RestView({ eng, nextLabel }) {
  const { el, spec, paused } = eng;
  const left = spec.duration - el;
  const c = eng.cur;
  const label = eng.kind === 'strength' ? 'Rest' : c.lastInRound ? `Round ${c.round} complete · Recovery` : eng.kind === 'race' ? 'Transition' : 'Recovery';
  return (
    <div className="text-center" data-testid="guided-rest">
      <div className="label-mini text-[var(--c-green-ink)]" data-testid="guided-rest-label">{label}</div>
      <div className="font-display font-black text-[80px] leading-none mt-3 tabular-nums" data-testid="guided-rest-time">{fmt(left)}</div>
      <div className="h-2 rounded-full bg-[var(--c-film)] mt-4 overflow-hidden"><div className="h-full bg-[var(--c-green)]" style={{ width: `${Math.min(100, (el / spec.duration) * 100)}%` }} /></div>
      <div className="text-sm text-muted-foreground mt-3" data-testid="guided-rest-next">Next: {nextLabel}</div>
      {paused && <div className="mt-3 pill bg-[#FFB800]/15 text-[#b57a00] inline-flex">Paused</div>}
      <div className="grid grid-cols-2 gap-2 mt-5">
        <Button variant="outline" onClick={() => eng.addRest(30)} data-testid="guided-add-rest" className="border-[var(--c-hair)] h-11"><Plus className="w-4 h-4 mr-1" /> 30s</Button>
        <Button variant="outline" onClick={eng.skipRest} data-testid="guided-skip-rest" className="border-[var(--c-hair)] h-11"><SkipForward className="w-4 h-4 mr-1" /> {eng.kind === 'race' ? "I'm ready" : 'Skip rest'}</Button>
      </div>
      <Controls paused={paused} onPause={eng.togglePause} onSkip={eng.skipExercise} onStop={eng.stop} />
    </div>
  );
}

export function SummaryView({ plan, setsDone, weights, durationSec, saving, onSave, onClose }) {
  // Circuits: one row per exercise, counting the rounds actually completed.
  const grouped = {};
  plan.exercises.forEach((p, i) => {
    const g = grouped[p.idx] || (grouped[p.idx] = { p: { ...p, sets: 0 }, sets: 0, reps: 0 });
    g.p.sets += p.sets; g.sets += setsDone[i]; g.reps += setsDone[i] * (p.target || 0);
  });
  const rows = Object.values(grouped);
  const totalSets = rows.reduce((s, r) => s + r.sets, 0);
  const totalReps = rows.filter(r => !['timed', 'open'].includes(r.p.tempo.kind)).reduce((s, r) => s + r.reps, 0);
  const unit = plan.kind === 'strength' ? 'sets' : 'rounds';
  return (
    <div data-testid="guided-summary">
      <div className="text-center">
        <div className="w-14 h-14 rounded-full bg-[var(--c-green-soft)] flex items-center justify-center mx-auto"><Check className="w-7 h-7 text-[var(--c-green-ink)]" strokeWidth={3} /></div>
        <div className="font-display font-black text-2xl mt-3">Session summary</div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4">
        {[['Duration', fmt(durationSec), 'sum-duration'], [plan.kind === 'strength' ? 'Sets' : 'Stations', totalSets, 'sum-sets'], ['Reps', totalReps, 'sum-reps']].map(([l, v, id]) => (
          <div key={l} className="regen-card p-3 text-center"><div className="label-mini">{l}</div><div className="font-mono text-xl mt-1 text-[#00C8FF]" data-testid={id}>{v}</div></div>
        ))}
      </div>
      <div className="mt-4 space-y-1.5" data-testid="sum-exercises">
        {rows.map(({ p, sets, reps }, i) => (
          <div key={i} className="flex items-center justify-between text-sm regen-card px-3 py-2">
            <span className="truncate">{p.name}</span>
            <span className="font-mono text-xs text-muted-foreground shrink-0">{sets}/{p.sets} {unit} · {p.tempo.kind === 'open' ? p.item.reps : p.tempo.kind === 'timed' ? `${p.target}s` : `${reps} reps`}{weights[i] ? ` · ${weights[i]}` : ''}</span>
          </div>
        ))}
      </div>
      <Button onClick={onSave} disabled={saving || totalSets === 0} data-testid="guided-save" className={`${BTN} mt-5 w-full bg-[var(--c-green)] hover:bg-[var(--c-green-soft)] text-[#081726]`}>{saving ? 'Saving…' : 'Save workout'}</Button>
      <Button variant="ghost" onClick={onClose} data-testid="guided-discard" className="w-full mt-1 text-muted-foreground">Close without saving</Button>
    </div>
  );
}

function Toggle({ label, checked, onChange, id }) {
  return <label className="flex items-center justify-between text-sm py-1.5"><span>{label}</span><Switch checked={checked} onCheckedChange={onChange} data-testid={id} /></label>;
}

export function CoachSettings({ s, set, onPreview }) {
  return (
    <div className="rounded-2xl border border-[var(--c-hair)] bg-[var(--c-film)] p-4 mb-4 space-y-2" data-testid="guided-settings">
      <Toggle label="Voice coaching" checked={s.enabled} onChange={v => set({ enabled: v })} id="set-voice-enabled" />
      <Toggle label="Motivation & technique cues" checked={s.motivation} onChange={v => set({ motivation: v })} id="set-motivation" />
      <Toggle label="Rest-period announcements" checked={s.restAnnouncements} onChange={v => set({ restAnnouncements: v })} id="set-rest-ann" />
      <div className="pt-1">
        <div className="label-mini mb-2">Voice</div>
        <div className="grid grid-cols-2 gap-1.5">
          {VOICE_OPTIONS.map(v => (
            <button key={v.id} onClick={() => { set({ voice: v.id }); onPreview(v.id); }} data-testid={`set-voice-${v.id}`}
              className={`text-left p-2 rounded-lg border text-xs transition-colors ${s.voice === v.id ? 'border-[#00C8FF] bg-[#00C8FF]/10' : 'border-[var(--c-hair)]'}`}>
              <div className="font-semibold">{v.label}</div><div className="text-muted-foreground text-[10px]">{v.desc}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="pt-2"><div className="flex justify-between label-mini"><span>Volume</span><span>{Math.round(s.volume * 100)}%</span></div>
        <Slider value={[s.volume]} min={0} max={1} step={0.05} onValueChange={([v]) => set({ volume: v })} className="mt-2" data-testid="set-volume" /></div>
      <div className="pt-2"><div className="flex justify-between label-mini"><span>Speaking speed</span><span>{s.rate.toFixed(2)}×</span></div>
        <Slider value={[s.rate]} min={0.8} max={1.3} step={0.05} onValueChange={([v]) => set({ rate: v, rateSet: true })} className="mt-2" data-testid="set-rate" />
        <p className="text-[10px] text-muted-foreground mt-1">Changes how fast the coach talks — never your exercise tempo.</p></div>
    </div>
  );
}
